import dayjs, { Dayjs } from "utils/dayjs"
import { ApiLinkEntity, ApiLinkRel, Purpose } from "config/constants"
import i18n from "intl/i18n"
import { toDateWithoutTimezone } from "utils/utils"
import { Guid, IApiLink } from "@/types"
import InvoicePositionBase, { IGetInvoicePositionBaseDto, InvoicePositionProcessingType, InvoicePositionUnit, isQuantityRequiredForInvoicePositionUnit } from "./InvoicePositionBase"
import TenantInvoicePosition, { InvoicePositionDistributionRule, InvoicePositionType } from "./TenantInvoicePosition"

export interface IGetReservationCartInvoicePositionDto extends IGetInvoicePositionBaseDto {
    id?: Guid
    reservationInvoicePositionId?: Guid | null
    amount?: number | null
    processingType?: InvoicePositionProcessingType | null
    start?: string | Dayjs | null
    end?: string | Dayjs | null,
    invoicedDate?: string | null
    fullyInvoiced?: boolean
    isAdHoc: boolean
    links: IApiLink[]
}

export interface ICreateReservationCartInvoiceDraft {
    draftId?: number
    start?: Dayjs | null
    invoicePosition: TenantInvoicePosition
    distributionRule: InvoicePositionDistributionRule
    type?: InvoicePositionType,
}

export interface IUpsertReservationCartInvoicePositionDto {
    unit: InvoicePositionUnit
    unitPrice: number
    unitQuantity?: number | null
    type: InvoicePositionType
    number: string
    text?: string | null
    distributionRule: InvoicePositionDistributionRule
    processingType?: InvoicePositionProcessingType | null
    start?: string | null
    end?: string | null
}

export interface IChangeReservationCartInvoicePosition {
    unit?: InvoicePositionUnit
    unitPrice?: number
    unitQuantity?: number | null
    type?: InvoicePositionType
    text?: string | null
    processingType?: InvoicePositionProcessingType | null
    start?: Dayjs | null
    end?: Dayjs | null
}

export default class ReservationCartInvoicePosition extends InvoicePositionBase {
    id?: Guid
    reservationInvoicePositionId?: Guid
    draftId?: number
    amount?: number
    invoicedDate?: Dayjs
    processingType?: InvoicePositionProcessingType
    start?: Dayjs
    end?: Dayjs
    fullyInvoiced: boolean
    isAdHoc: boolean

    linkUpdate?: IApiLink
    linkDelete?: IApiLink

    #purpose: Purpose
    #initialAmount?: number
    #initialType?: InvoicePositionType

    constructor({
        dto,
        purpose,
    }: {
        dto: IGetReservationCartInvoicePositionDto | ICreateReservationCartInvoiceDraft,
        purpose: Purpose
    }) {
        super({
            number: 'number' in dto ? dto.number : dto.invoicePosition.number,
            text: 'text' in dto ? dto.text : undefined,
            distributionRule: dto.distributionRule,
            unit: 'unit' in dto ? dto.unit : undefined,
            unitPrice: 'unitPrice' in dto ? dto.unitPrice : undefined,
            unitQuantity: 'unitQuantity' in dto ? dto.unitQuantity : undefined,
        })
        this.id = 'id' in dto ? dto.id : undefined
        this.reservationInvoicePositionId = 'reservationInvoicePositionId' in dto ? (dto.reservationInvoicePositionId ?? undefined) : undefined
        this.draftId = 'draftId' in dto ? dto.draftId : undefined
        this.amount = 'amount' in dto ? (typeof dto.amount === "number" ? dto.amount : undefined) : undefined
        this.type = 'type' in dto ? (dto.type || undefined) : undefined
        this.processingType = 'processingType' in dto ? (dto.processingType || undefined) : undefined
        this.invoicedDate = 'invoicedDate' in dto ? (dto.invoicedDate ? dayjs.utc(dto.invoicedDate, "YYYY-MM-DD").startOf('date') : undefined) : undefined
        this.start = 'start' in dto && dto.start ? (dayjs.isDayjs(dto.start) ? dto.start : dayjs.utc(dto.start, "YYYY-MM-DD").startOf('date')) : undefined
        this.end = 'end' in dto && dto.end ? (dayjs.isDayjs(dto.end) ? dto.end : dayjs.utc(dto.end, "YYYY-MM-DD").startOf('date')) : undefined
        this.fullyInvoiced = 'fullyInvoiced' in dto ? (dto.fullyInvoiced || false) : false
        this.isAdHoc = 'isAdHoc' in dto ? dto.isAdHoc : false

        this.linkUpdate = 'links' in dto ? dto.links.find(lnk => lnk.entity === ApiLinkEntity.invoicePosition && lnk.rel === ApiLinkRel.update) : undefined
        this.linkDelete = 'links' in dto ? dto.links.find(lnk => lnk.entity === ApiLinkEntity.invoicePosition && lnk.rel === ApiLinkRel.delete) : undefined

        this.#purpose = purpose
        this.#initialAmount = this.amount
        this.#initialType = this.type
        this.invoicePosition = 'invoicePosition' in dto ? dto.invoicePosition : undefined
    }

    update(changes: IChangeReservationCartInvoicePosition) {
        for (const key in changes) {
            if (changes.hasOwnProperty(key)) {
                this[key] = changes[key]
            }
        }
    }

    toUpsertDtoIfPossible(changes: IChangeReservationCartInvoicePosition): IUpsertReservationCartInvoicePositionDto | null {
        const dto = {
            unit: this.unit,
            unitPrice: this.unitPrice,
            unitQuantity: this.unitQuantity,
            type: this.type,
            number: this.number,
            text: this.customText,
            distributionRule: this.distributionRule,
            processingType: this.processingType || null,
            start: this.start ? toDateWithoutTimezone(this.start) : null,
            end: this.end ? toDateWithoutTimezone(this.end) : null,
        }

        for (const key in changes) {
            if (changes.hasOwnProperty(key)) {
                dto[key] = changes[key]
            }
        }

        if (typeof dto.type === 'undefined'
            || typeof dto.unit === 'undefined'
            || typeof dto.unitPrice === 'undefined'
            || (isQuantityRequiredForInvoicePositionUnit(dto.unit) && typeof dto.unitQuantity !== 'number')) {
            return null
        }

        return dto as IUpsertReservationCartInvoicePositionDto
    }

    get isDraft(): boolean {
        return !this.id
    }

    get isSale(): boolean {
        return this.#purpose === Purpose.sale
    }

    get isWithType(): boolean {
        return !this.isSale
    }

    isValid(reservationStart?: Dayjs, reservationEnd?: Dayjs, lastNotCreditedInvoiceEndDate?: Dayjs): boolean {
        return typeof this.amount === "number"
            && !!this.type
            && !!this.unit
            && typeof this.unitPrice === "number"
            && (isQuantityRequiredForInvoicePositionUnit(this.unit) ? typeof this.unitQuantity === "number" : true)
            && !this.hasInvalidPeriod(reservationStart, reservationEnd, lastNotCreditedInvoiceEndDate)
    }

    get isAmountOrTypeChanged() {
        return this.#initialAmount !== this.amount
            || this.#initialType !== this.type
    }

    get invoicedLabel(): string | null {
        if (!this.invoicedDate) return null

        if (this.type === InvoicePositionType.single) {
            return i18n.t('reservation.invoicedOnDate', { date: this.invoicedDate.format(i18n.t('config.dateLongFormat')) })
        } else if (this.type === InvoicePositionType.recurring) {
            return i18n.t('reservation.invoicedUntilDate', { date: this.invoicedDate.format(i18n.t('config.dateLongFormat')) })
        }

        return null
    }

    hasInvalidPeriod(reservationStart?: Dayjs, reservationEnd?: Dayjs, lastNotCreditedInvoiceEndDate?: Dayjs): boolean {
        const resStart = reservationStart?.clone().startOf('date')
        const resEnd = reservationEnd?.clone().startOf('date')

        const isNotInvoicedSingleIPInFullyInvoicedCR = !!resStart && !!resEnd && !!lastNotCreditedInvoiceEndDate
            && resEnd.isSame(lastNotCreditedInvoiceEndDate, "date")
            && !this.invoicedDate
            && this.type === InvoicePositionType.single
            && !this.start
            && !this.end

        if (isNotInvoicedSingleIPInFullyInvoicedCR) {
            return true
        }

        if ((!resStart && !resEnd)
            || (!this.start && !this.end)) return false

        // make sure that start date is not same or earlier than last invoice end date
        // (consider not invoiced positions only)
        if (lastNotCreditedInvoiceEndDate && !this.invoicedDate && this.start && this.start.isSameOrBefore(lastNotCreditedInvoiceEndDate)) {
            return true
        }

        // make sure that start date is not later than end date
        if (this.start && this.end && this.start.isAfter(this.end)) {
            return true
        }

        // make sure that start date is before end date
        // (if invoice position date is missing, use reservation date instead)
        if ((
            !this.start
            && resStart
            && this.end
            && this.end.isBefore(resStart)
        )
            || (
                !this.end
                && resEnd
                && this.start
                && this.start.isAfter(resEnd)
            )) {
            return true
        }

        if (resStart && resEnd) {
            return this.start?.isBefore(resStart)
                || this.end?.isAfter(resEnd)
                || false
        } else if (resStart) {
            return this.start?.isBefore(resStart)
                || false
        }

        return false
    }

}
